import React, { useState, useMemo, useEffect, createRef } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Button
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  Column,
  Paging,
  SearchPanel,
  Sorting,
  FilterPanel,
  HeaderFilter,
  FilterBuilder,
  Lookup,
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { columnSchema } from './gridConfig';
import {
  fetchRejectedImportsOpportunities,
  appNotify
} from 'store/actions';
import { importModuleStateSelector } from 'store/selectors';
import { dataGridExtension } from 'components/devExtreme';
import DataGrid from 'components/devExtreme/DataGrid';
import CustomColumnChooser from 'components/customColumnChooser';
import { sendClientLog } from 'services/logService';
import { push } from 'connected-react-router';
import { Constants } from 'utilities/constants';
import { getWithToken, postWithToken } from 'api/network';
import isPlainObject from 'lodash/isPlainObject';

const LOAD_IMPORT = 'Load Import';

const styles = theme => ({
  gridPaper: {
    padding: '5px 8px 8px',
    overflow: 'hidden',
    width: '100%'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    // width: '66%'
  },
  title: {
    margin: '5px 0 8px 0'
  },
  refreshButton: {
    marginLeft: 8
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  },
  flexContainer: {
    display: 'flex'
  }
});

const gridRef = createRef();

const RejectedImportsGrid = ({
  classes,
  fetchRejectedImportsOpportunities,
  isLoading,
  push,
  appNotify
}) => {
  const [dataSource, setDataSource] = useState(null);
  const [createdByDataSource, setCreatedByDataSource] = useState(null);
  const [channelLookupDataSource, setChannelLookupDataSource] = useState(null);
  const [isColumnChooserVisible, setIsColumnChooserVisible] = useState(false);

  useEffect(() => {
    createDataSource();
    createCreatedByLookupDataSource();
    createChannelLookupDataSource();
  }, []);

  function createDataSource() {
    const url='/api/underwrite/import/rejectedhistory';
    const store = new CustomStore({
      key: 'id',
      load: function(loadOptions) {
        if (isPlainObject(loadOptions.group)) {
          loadOptions.group = [loadOptions.group];
        }

        let params = {};

        dataGridExtension.customStoreRequestParams.forEach(i => {
          if (i in loadOptions && dataGridExtension.isNotEmpty(loadOptions[i])) {
            params[i]= loadOptions[i];
          }
        });

        return postWithToken(url, params)
          .then(response => response.json())
          .then((data) => {
            return {
              data: data.data,
              totalCount: data.totalCount ?? 10000,
            };
          }).catch((error) => {
            appNotify(`Failed to fetch imports: ${error.message}`, 'error');
          })
      }
    });

    setDataSource(store);
  }

  function createCreatedByLookupDataSource() {
    const store = new CustomStore({
      key: 'value',
      load: function(loadOptions) {
        const filter = dataGridExtension.getLookupFilter(loadOptions);
        const url = `/api/underwrite/import/rejectedhistory/createdByLookup${filter}`;

        return getWithToken(url)
          .then(response => response.json())
          .then((data) => {
            return {
              data: data.data,
            };
          }).catch((error) => {
            appNotify(`Failed to fetch imports created by lookup: ${error.message}`, 'error');
          })
      }
    });

    setCreatedByDataSource(store);
  }

  function createChannelLookupDataSource() {
    const store = new CustomStore({
      key: 'value',
      load: function(loadOptions) {
        const filter = dataGridExtension.getLookupFilter(loadOptions);
        const url = `/api/underwrite/import/rejectedhistory/channelLookup${filter}`;

        return getWithToken(url)
          .then(response => response.json())
          .then((data) => {
            return {
              data: data.data,
            };
          })
          .catch((error) => {
            appNotify(`Failed to fetch imports channel lookup: ${error.message}`, 'error');
          })
      }
    });

    setChannelLookupDataSource(store);
  }

  const getLoadImportColumnRender = cell => {
    if (cell.data.rejectedCount) {
      return <div><Button size="small" color="primary" onClick={() => handleRedirectToOpportunityImport(cell.data)}>Load in Import Grid</Button></div>;

    }
    return null;
  }

  function handleRedirectToOpportunityImport(importRecord) {
    fetchRejectedImportsOpportunities(importRecord.id, () => {
      push(Constants.Routes.UNDERWRITE_IMPORT_OPPORTUNITY);
    });
  }

  const refreshButtonClick = () => {
    createDataSource();
    createCreatedByLookupDataSource();
    createChannelLookupDataSource();
    sendClientLog('Info', 'Rejected Imports Grid - User started data fetch - refresh button');
  }

  const onToolbarPreparing = e => {
    // this adds a column chooser icon so we can customize its behaviour
    e.toolbarOptions.items.unshift({
      widget: "dxButton",
      location: "after",
      options: {
        icon: "columnchooser",
        elementAttr: {
          id: "rejectedGridColumnChooserBtn"
        },
        onClick: () => setIsColumnChooserVisible(true)
      }
    });
  }

  // solution from devextreme support team https://supportcenter.devexpress.com/ticket/details/t998890/datagrid-column-headerfilter-customization
  function calculateFilterExpression(
    filterValue,
    selectedFilterOperation,
    target
  ) {
    if (target === 'headerFilter') {
      return [this.dataField, selectedFilterOperation, filterValue];
    }

    return this.defaultCalculateFilterExpression.apply(this, arguments);
  }

  const columns = useMemo(() => {
    const columnList = columnSchema.map(({ field, displayName, width, type, format, visible, sortIndex, sortOrder }) => {

      if (['channel', 'createdUserId'].includes(field)) {
        let columnDataSource;
        switch (field) {
          case 'channel':
            columnDataSource = channelLookupDataSource;
            break;
          case 'createdUserId':
            columnDataSource = createdByDataSource;
            break;
        }

        return (
          <Column
            key={field}
            dataField={field}
            caption={displayName}
            minWidth={width}
            dataType={type}
            format={format}
            visible={visible !== false}
            sortIndex={sortIndex}
            sortOrder={sortOrder}
            headerFilter={ { dataSource: columnDataSource } }
          >
            <Lookup
              dataSource={columnDataSource}
              displayExpr="text"
              valueExpr="value"
            />
          </Column>
        )
      }

      // original solutions for custom implementation from devextreme support team
      // https://supportcenter.devexpress.com/ticket/details/t1014077/datagrid-header-filter-performance-for-large-client-side-dataset
      // https://supportcenter.devexpress.com/ticket/details/t1004692/datagrid-searching-does-not-work-when-header-filter-date-values-are-ungrouped
      const headerDataSource =  dataGridExtension.createHeaderDataSource('/api/underwrite/import/rejectedhistory', type, field, gridRef);

      return (
          <Column
            key={field}
            dataField={field}
            caption={displayName}
            minWidth={width}
            dataType={type}
            format={format}
            visible={visible !== false}
            sortIndex={sortIndex}
            sortOrder={sortOrder}
            calculateFilterExpression={calculateFilterExpression}
          >
            <HeaderFilter dataSource={headerDataSource} groupInterval={null} />
          </Column>
        )
      });

      columnList.push(
        <Column
          key={LOAD_IMPORT}
          dataField="loadImport"
          name={LOAD_IMPORT}
          caption={LOAD_IMPORT}
          minWidth={40}
          dataType='string'
          visible={true}
          cellRender={getLoadImportColumnRender}
          allowFiltering={false}
          allowSorting={false}
        />
      );

      return columnList;
    }, [dataSource]);

  const fields = useMemo(() => {
    return dataGridExtension.sortFields(columnSchema);
  }, [columnSchema]);

  return (
    <div className={classes.container}>
      <div className={classes.titleContainer}>
        <div className={classes.flexContainer}>
          <Typography variant='h5' className={classes.title}>Rejected Imports</Typography>
          <Tooltip title='Refresh Data'>
            <span>
              <IconButton disabled={isLoading} onClick={refreshButtonClick} className={classes.refreshButton}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
      <Paper className={classes.gridPaper}>
        <DataGrid
          id='rejectedImportsGrid'
          ref={gridRef}
          persistState
          dataSource={dataSource}
          focusedRowEnabled={false}
          height={'calc(95vh - 140px)'}
          remoteOperations={true}
          onToolbarPreparing={onToolbarPreparing}
          enableTooltips
          tooltips={columnSchema}
        >
          <HeaderFilter visible={true} search={{ enabled: true }} hideSelectAllOnSearch={false} />
          <Sorting mode={'multiple'} />
          <FilterPanel visible={true} />
          <FilterBuilder fields={fields}>
            {dataGridExtension.filterBuilderCustomOperations}
          </FilterBuilder>
          <Paging
            defaultPageSize={100}
            enabled={true}
          />
          <SearchPanel visible={true} searchVisibleColumnsOnly={true} />
          {columns}
          <Column width='auto' showInColumnChooser={false} visibleIndex={9999} />
        </DataGrid>
        <CustomColumnChooser
          container="#rejectedGrid"
          visible={isColumnChooserVisible}
          onHiding={() => setIsColumnChooserVisible(false)}
          columns={columns}
          gridRef={gridRef}
        />
      </Paper>
    </div>
  )
}

const mapStateToProps = state => {
  const underwriteState = importModuleStateSelector(state, Constants.Module.UNDERWRITE);
  return {
    isLoading: underwriteState.importLoadingOverlayState.isLoading
  }
}
const mapDispatchToProps = dispatch => {
  return {
    fetchRejectedImportsOpportunities: (id, callback) => dispatch(fetchRejectedImportsOpportunities({ id, callback })),
    push: route => dispatch(push(route)),
    appNotify: (message, variant, options) => dispatch(appNotify(message, variant, options))
  }
}
export default compose(withStyles(styles), connect(mapStateToProps, mapDispatchToProps))(RejectedImportsGrid);
